import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import axios from 'axios';
import { Store, DollarSign, Package, FileText, TrendingUp, RefreshCw, Search, Plus, X, Calendar, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_PRIMARY = '#2b3380';

function StoreOperations({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dailySales, setDailySales] = useState([]);
  const [grnList, setGrnList] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSalesForm, setShowSalesForm] = useState(false);
  const [showGrnForm, setShowGrnForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [salesForm, setSalesForm] = useState({
    sale_date: new Date().toISOString().split('T')[0],
    gross_sales: '',
    returns: 0,
    discounts: 0,
    cash_sales: '',
    upi_sales: '',
    card_sales: '',
    devices_sold: 0,
    accessories_sold: 0,
    repairs_done: 0,
    footfall: 0
  });

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: TrendingUp },
    { id: 'daily-sales', name: 'Daily Sales', icon: DollarSign },
    { id: 'grn', name: 'GRN & Stock', icon: Package },
    { id: 'reconciliation', name: 'Reconciliation', icon: FileText },
    { id: 'reports', name: 'Reports', icon: TrendingUp }
  ];

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  useEffect(() => {
    if (selectedStore) {
      if (activeTab === 'dashboard') fetchDashboardStats();
      if (activeTab === 'daily-sales') fetchDailySales();
      if (activeTab === 'grn') fetchGrnList();
      if (activeTab === 'reconciliation') fetchReconciliations();
    }
  }, [selectedStore, activeTab]);

  const fetchStores = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data.stores || []);
      if (response.data.stores?.length > 0) {
        setSelectedStore(response.data.stores[0]);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/store-operations/dashboard?store_id=${selectedStore.store_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const fetchDailySales = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/daily-sales?store_id=${selectedStore.store_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDailySales(response.data.sales || []);
    } catch (error) {
      console.error('Error fetching sales:', error);
    }
  };

  const fetchGrnList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/grn?store_id=${selectedStore.store_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGrnList(response.data.grns || []);
    } catch (error) {
      console.error('Error fetching GRN:', error);
    }
  };

  const fetchReconciliations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/daily-reconciliation?store_id=${selectedStore.store_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReconciliations(response.data.reconciliations || []);
    } catch (error) {
      console.error('Error fetching reconciliations:', error);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const handleRecordSales = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/daily-sales`, {
        store_id: selectedStore.store_id,
        ...salesForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Daily sales recorded successfully!');
      setShowSalesForm(false);
      fetchDailySales();
      fetchDashboardStats();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const refreshData = () => {
    if (selectedStore) {
      fetchDashboardStats();
      if (activeTab === 'daily-sales') fetchDailySales();
      if (activeTab === 'grn') fetchGrnList();
      if (activeTab === 'reconciliation') fetchReconciliations();
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Submitted': 'border-l-green-500 bg-green-50',
      'Pending': 'border-l-yellow-500 bg-yellow-50',
      'Approved': 'border-l-blue-500 bg-blue-50',
      'Verified': 'border-l-green-500 bg-green-50',
      'Rejected': 'border-l-red-500 bg-red-50'
    };
    return colors[status] || 'border-l-gray-400 bg-gray-50';
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Submitted': 'bg-green-500 text-white',
      'Pending': 'bg-yellow-500 text-white',
      'Approved': 'bg-blue-500 text-white',
      'Verified': 'bg-green-500 text-white',
      'Rejected': 'bg-red-500 text-white'
    };
    return colors[status] || 'bg-gray-400 text-white';
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6">
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-3 text-gray-500">Loading stores...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: ZOBOX_PRIMARY}}>🏪 Store Operations</h1>
            <p className="text-gray-600 mt-2">Manage daily store operations, sales, and inventory</p>
          </div>
          <div className="flex gap-3 items-center">
            {/* Store Selector */}
            <select
              value={selectedStore?.store_id || ''}
              onChange={(e) => {
                const store = stores.find(s => s.store_id === e.target.value);
                setSelectedStore(store);
              }}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 min-w-[200px]"
            >
              {stores.map(store => (
                <option key={store.store_id} value={store.store_id}>
                  {store.store_name} ({store.store_code})
                </option>
              ))}
            </select>
            <button
              onClick={refreshData}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={() => setShowSalesForm(true)}
              className="px-6 py-2 rounded-lg text-white font-medium flex items-center gap-2"
              style={{backgroundColor: ZOBOX_PRIMARY}}
            >
              <Plus className="w-5 h-5" />
              Record Sales
            </button>
          </div>
        </div>

        {/* Store Info Card */}
        {selectedStore && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6 border-l-4" style={{borderLeftColor: ZOBOX_PRIMARY}}>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full" style={{backgroundColor: '#e8eaf6'}}>
                  <Store className="w-6 h-6" style={{color: ZOBOX_PRIMARY}} />
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{color: ZOBOX_PRIMARY}}>{selectedStore.store_name}</h2>
                  <p className="text-sm text-gray-500">{selectedStore.store_code} • {selectedStore.city}</p>
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${selectedStore.status === 'Active' ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}`}>
                {selectedStore.status}
              </span>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full" style={{backgroundColor: '#e8eaf6'}}>
              <DollarSign className="w-6 h-6" style={{color: ZOBOX_PRIMARY}} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{color: ZOBOX_PRIMARY}}>
                ₹{dashboardStats?.today_sales?.toLocaleString() || '0'}
              </p>
              <p className="text-sm text-gray-500">Today's Sales</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-100">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                ₹{dashboardStats?.month_sales?.toLocaleString() || '0'}
              </p>
              <p className="text-sm text-gray-500">Month Sales</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-yellow-100">
              <Package className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">{dashboardStats?.pending_grns || 0}</p>
              <p className="text-sm text-gray-500">Pending GRNs</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-orange-100">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">{dashboardStats?.pending_reconciliations || 0}</p>
              <p className="text-sm text-gray-500">Pending Recos</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex border-b overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-6 py-3 font-medium flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id ? 'border-b-2' : 'text-gray-500'}`}
                  style={activeTab === tab.id ? {borderColor: ZOBOX_PRIMARY, color: ZOBOX_PRIMARY} : {}}
                >
                  <Icon className="w-4 h-4" /> {tab.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Search Bar */}
        {activeTab !== 'dashboard' && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold mb-4" style={{color: ZOBOX_PRIMARY}}>Quick Actions</h3>
              <div className="grid grid-cols-2 gap-4">
                <button 
                  onClick={() => setShowSalesForm(true)}
                  className="p-4 border rounded-lg hover:bg-gray-50 text-left"
                >
                  <DollarSign className="w-6 h-6 mb-2" style={{color: ZOBOX_PRIMARY}} />
                  <p className="font-medium">Record Sales</p>
                  <p className="text-sm text-gray-500">Enter today's sales</p>
                </button>
                <button 
                  onClick={() => handleTabChange('grn')}
                  className="p-4 border rounded-lg hover:bg-gray-50 text-left"
                >
                  <Package className="w-6 h-6 mb-2 text-green-600" />
                  <p className="font-medium">Create GRN</p>
                  <p className="text-sm text-gray-500">Receive new stock</p>
                </button>
                <button 
                  onClick={() => handleTabChange('reconciliation')}
                  className="p-4 border rounded-lg hover:bg-gray-50 text-left"
                >
                  <FileText className="w-6 h-6 mb-2 text-orange-600" />
                  <p className="font-medium">Daily Reco</p>
                  <p className="text-sm text-gray-500">Submit reconciliation</p>
                </button>
                <button 
                  onClick={() => handleTabChange('reports')}
                  className="p-4 border rounded-lg hover:bg-gray-50 text-left"
                >
                  <TrendingUp className="w-6 h-6 mb-2 text-blue-600" />
                  <p className="font-medium">View Reports</p>
                  <p className="text-sm text-gray-500">Analytics & insights</p>
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-bold mb-4" style={{color: ZOBOX_PRIMARY}}>Recent Activity</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="font-medium">Sales Recorded</p>
                    <p className="text-sm text-gray-500">Today at 10:30 AM</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Package className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-medium">GRN Received</p>
                    <p className="text-sm text-gray-500">Yesterday at 4:15 PM</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <FileText className="w-5 h-5 text-orange-500" />
                  <div>
                    <p className="font-medium">Reconciliation Approved</p>
                    <p className="text-sm text-gray-500">2 days ago</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'daily-sales' && (
          <div className="space-y-4">
            {dailySales.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <DollarSign className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-xl font-medium text-gray-500 mb-2">No sales records found</p>
                <p className="text-gray-400">Click "Record Sales" to add daily sales</p>
              </div>
            ) : (
              dailySales.map((sale, idx) => (
                <div key={sale.id || idx} className={`bg-white rounded-lg shadow-md p-6 border-l-4 hover:shadow-lg transition-shadow ${getStatusColor(sale.status)}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold" style={{color: ZOBOX_PRIMARY}}>
                          {new Date(sale.sale_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(sale.status)}`}>
                          {sale.status}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{color: ZOBOX_PRIMARY}}>₹{sale.net_sales?.toLocaleString()}</p>
                      <p className="text-sm text-gray-500">Net Sales</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div><strong>Gross:</strong> ₹{sale.gross_sales?.toLocaleString()}</div>
                    <div><strong>Cash:</strong> ₹{sale.cash_sales?.toLocaleString()}</div>
                    <div><strong>UPI:</strong> ₹{sale.upi_sales?.toLocaleString()}</div>
                    <div><strong>Card:</strong> ₹{sale.card_sales?.toLocaleString()}</div>
                  </div>
                  <div className="flex gap-4 mt-3 pt-3 border-t text-sm text-gray-500">
                    <span>Devices: {sale.devices_sold}</span>
                    <span>Accessories: {sale.accessories_sold}</span>
                    <span>Repairs: {sale.repairs_done}</span>
                    <span>Footfall: {sale.footfall}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'grn' && (
          <div className="space-y-4">
            {grnList.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-xl font-medium text-gray-500 mb-2">No GRN records found</p>
                <p className="text-gray-400">Create a GRN when you receive new stock</p>
              </div>
            ) : (
              grnList.map((grn, idx) => (
                <div key={grn.id || idx} className={`bg-white rounded-lg shadow-md p-6 border-l-4 hover:shadow-lg transition-shadow ${getStatusColor(grn.status)}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold" style={{color: ZOBOX_PRIMARY}}>{grn.grn_number}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(grn.status)}`}>
                          {grn.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">Supplier: {grn.supplier}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold" style={{color: ZOBOX_PRIMARY}}>₹{grn.total_value?.toLocaleString()}</p>
                      <p className="text-sm text-gray-500">{grn.total_items} items</p>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm text-gray-500 pt-3 border-t">
                    <span><Calendar className="w-4 h-4 inline mr-1" />Invoice: {grn.invoice_date}</span>
                    <span>Received: {grn.received_date}</span>
                    <span>By: {grn.received_by}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'reconciliation' && (
          <div className="space-y-4">
            {reconciliations.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-xl font-medium text-gray-500 mb-2">No reconciliation records found</p>
                <p className="text-gray-400">Submit daily reconciliation to track discrepancies</p>
              </div>
            ) : (
              reconciliations.map((reco, idx) => (
                <div key={reco.id || idx} className={`bg-white rounded-lg shadow-md p-6 border-l-4 hover:shadow-lg transition-shadow ${getStatusColor(reco.status)}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold" style={{color: ZOBOX_PRIMARY}}>
                          {new Date(reco.reconciliation_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(reco.status)}`}>
                          {reco.status}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      {reco.discrepancy_amount > 0 ? (
                        <p className="text-xl font-bold text-red-500">-₹{reco.discrepancy_amount?.toLocaleString()}</p>
                      ) : (
                        <p className="text-xl font-bold text-green-500">No Discrepancy</p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div><strong>System Sales:</strong> ₹{reco.system_sales?.toLocaleString()}</div>
                    <div><strong>Reported Sales:</strong> ₹{reco.reported_sales?.toLocaleString()}</div>
                    <div><strong>Cash in Hand:</strong> ₹{reco.cash_in_hand?.toLocaleString()}</div>
                  </div>
                  <div className="flex gap-4 text-sm text-gray-500 pt-3 border-t">
                    <span>Submitted by: {reco.submitted_by}</span>
                    {reco.approved_by && <span>Approved by: {reco.approved_by}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <TrendingUp className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-xl font-medium text-gray-500 mb-2">Reports Coming Soon</p>
            <p className="text-gray-400">Detailed analytics and insights will be available here</p>
          </div>
        )}

        {/* Record Sales Modal */}
        {showSalesForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: ZOBOX_PRIMARY}}>💰 Record Daily Sales</h2>
                  <button onClick={() => setShowSalesForm(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="font-medium">{selectedStore?.store_name}</p>
                  <p className="text-sm text-gray-500">{selectedStore?.store_code}</p>
                </div>

                <form onSubmit={handleRecordSales}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Sale Date *</label>
                      <input
                        type="date"
                        value={salesForm.sale_date}
                        onChange={(e) => setSalesForm({...salesForm, sale_date: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Gross Sales (₹) *</label>
                        <input
                          type="number"
                          value={salesForm.gross_sales}
                          onChange={(e) => setSalesForm({...salesForm, gross_sales: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Returns (₹)</label>
                        <input
                          type="number"
                          value={salesForm.returns}
                          onChange={(e) => setSalesForm({...salesForm, returns: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Discounts (₹)</label>
                        <input
                          type="number"
                          value={salesForm.discounts}
                          onChange={(e) => setSalesForm({...salesForm, discounts: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Cash Sales (₹)</label>
                        <input
                          type="number"
                          value={salesForm.cash_sales}
                          onChange={(e) => setSalesForm({...salesForm, cash_sales: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">UPI Sales (₹)</label>
                        <input
                          type="number"
                          value={salesForm.upi_sales}
                          onChange={(e) => setSalesForm({...salesForm, upi_sales: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Card Sales (₹)</label>
                        <input
                          type="number"
                          value={salesForm.card_sales}
                          onChange={(e) => setSalesForm({...salesForm, card_sales: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Devices Sold</label>
                        <input
                          type="number"
                          value={salesForm.devices_sold}
                          onChange={(e) => setSalesForm({...salesForm, devices_sold: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Accessories</label>
                        <input
                          type="number"
                          value={salesForm.accessories_sold}
                          onChange={(e) => setSalesForm({...salesForm, accessories_sold: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Repairs</label>
                        <input
                          type="number"
                          value={salesForm.repairs_done}
                          onChange={(e) => setSalesForm({...salesForm, repairs_done: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Footfall</label>
                        <input
                          type="number"
                          value={salesForm.footfall}
                          onChange={(e) => setSalesForm({...salesForm, footfall: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
                        style={{backgroundColor: submitting ? '#9e9e9e' : ZOBOX_PRIMARY}}
                      >
                        <DollarSign className="w-5 h-5" />
                        {submitting ? 'Recording...' : 'Record Sales'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowSalesForm(false)}
                        className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default StoreOperations;
